import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Wordmark from '../components/Wordmark';
import { recordPendingConsent } from '../lib/termsVersion';
import { GOOGLE_OAUTH_CLIENT_ID } from '../lib/firebaseConfig';

/**
 * LoginPage — three sign-in options stacked by likely-friction:
 *   1. Google OAuth (one tap, primary)
 *   2. Email magic link (no password, captures email for later)
 *   3. Guest / anonymous (zero friction, data lives on this device only)
 *
 * Props are split per provider so the page doesn't need to know about
 * Firebase internals — it just calls the right callback when the user
 * picks an option. useAuth.js handles the Firebase wiring.
 *
 * Walter 2026-05-15: click-accept Terms checkbox gates all three
 * sign-in paths. Buttons are disabled until checked. On click, we
 * stash a pending-consent record in localStorage; App.js flushes it
 * to Firestore once the user.uid is available (handles the OAuth
 * redirect + magic-link round-trip cases where consent is captured
 * before auth completes).
 */
export default function LoginPage({
  onGoogleSignIn,
  onGoogleIdTokenSignIn,
  onGuestSignIn,
  onMagicLinkRequest,
  // Walter 2026-06-06: when new sign-ups are paused (config/app.
  // acceptingSignups === false), existing users still sign in normally
  // via Google / magic-link above — only the guest "try without an
  // account" slot is swapped for a "join the waitlist" action so the
  // casual new-account path is gated without locking anyone out.
  signupsPaused = false,
  onJoinWaitlist,
}) {
  // 'idle' | 'sending' | 'sent' — drives the magic-link UI state.
  const [magicState, setMagicState] = useState('idle');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Briefly highlight the checkbox if a user tries to click a disabled
  // sign-in button — visual nudge toward what they need to do.
  const [shakeTerms, setShakeTerms] = useState(false);

  // Walter 2026-05-16: GIS button container ref. The Google Identity
  // Services library renders the official "Sign in with Google" button
  // into this div via google.accounts.id.renderButton. Using the
  // Google-branded button (instead of our orange-accent custom button)
  // because GIS's ID-token flow bypasses Firebase's redirect-based
  // sign-in path that breaks on Android Chrome (popup gets promoted
  // to a new tab → sessionStorage partition → "missing initial
  // state"). The GIS button is recognized by users and works
  // everywhere — Android Chrome, iOS Safari, desktop browsers.
  const gisButtonRef = useRef(null);
  const [gisReady, setGisReady] = useState(false);
  const [gisError, setGisError] = useState(null);
  // Hide the GIS button until we know it'll work — if the OAuth
  // client ID is still the placeholder, fall back to the legacy
  // signInWithPopup button below. Lets us ship the wiring without
  // breaking existing flows pre-config.
  const gisConfigured = GOOGLE_OAUTH_CLIENT_ID
    && !GOOGLE_OAUTH_CLIENT_ID.startsWith('REPLACE_ME');

  // GIS callback — receives a credential object containing a JWT
  // ID token. Hand it to Firebase Auth via signInWithCredential
  // (wired in useAuth as onGoogleIdTokenSignIn). The pending-consent
  // record was already stashed before the user tapped the GIS
  // button via the click-capture handler below.
  const handleGISCredential = async (response) => {
    if (!response?.credential) {
      setGisError('Google sign-in returned no credential. Try again or use email.');
      return;
    }
    // Stash the pending Terms consent BEFORE signing in. App.js's
    // post-auth effect will flush it to Firestore once user.uid is
    // available. Same pattern as the other sign-in paths.
    recordPendingConsent();
    try {
      if (onGoogleIdTokenSignIn) {
        await onGoogleIdTokenSignIn(response.credential);
      }
    } catch (err) {
      console.error('GIS Firebase sign-in failed:', err);
      // Walter 2026-05-25 (Bug #11 — round-2 tester report): specific
      // copy for the self-deleted / suspended case so the user knows
      // why and what to do next (recovery email or wait out the 7-day
      // window). The bare "Sign-in failed" message left users stuck.
      const code = err?.code || '';
      if (code === 'auth/user-disabled' || code === 'auth/user-not-found') {
        setGisError(
          'This Google account was used to delete a listicity account in the last 7 days. ' +
          'Check your inbox for the recovery email — clicking the link there restores the account. ' +
          'After 7 days you can sign up fresh.'
        );
      } else if (code === 'auth/network-request-failed') {
        setGisError('Network error — check your connection and try again.');
      } else {
        setGisError('Sign-in failed. Try again or use email below.');
      }
    }
  };

  // Initialize GIS once the script has loaded and we have a real
  // client ID. The script is loaded async/defer from index.html, so
  // it may not be ready on first render — poll briefly until it is.
  useEffect(() => {
    if (!gisConfigured) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tryInit = () => {
      if (cancelled) return;
      const gis = window.google?.accounts?.id;
      if (!gis) {
        attempts += 1;
        if (attempts < 50) setTimeout(tryInit, 100);
        return;
      }
      try {
        gis.initialize({
          client_id: GOOGLE_OAUTH_CLIENT_ID,
          callback: handleGISCredential,
          // ux_mode: 'popup' uses GIS's own iframe-based popup which
          // doesn't have the sessionStorage partition problem
          // Firebase's signInWithPopup has.
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        setGisReady(true);
      } catch (err) {
        console.warn('GIS initialize failed:', err);
        setGisError('Google sign-in unavailable. Use email below.');
      }
    };
    tryInit();
    return () => { cancelled = true; };
    // NOTE: handleGISCredential intentionally excluded from deps —
    // it captures onGoogleIdTokenSignIn which is stable in practice
    // (useCallback'd in useAuth). Including it would re-init GIS on
    // every parent re-render, which is wasteful.
  }, [gisConfigured]);

  // Render the GIS button into our container once GIS is initialized.
  // Re-render when agreedToTerms flips so the button reflects whether
  // the user has consented. (GIS doesn't natively disable; we wrap it
  // in a click-capture handler below.)
  useEffect(() => {
    if (!gisReady || !gisButtonRef.current) return;
    const gis = window.google?.accounts?.id;
    if (!gis) return;
    try {
      // Clear any prior rendering — GIS doesn't dedupe.
      gisButtonRef.current.innerHTML = '';
      gis.renderButton(gisButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        // Match the width of the email button below for visual
        // continuity. The parent container is the LoginPage card
        // which clamps to ~340px wide; 320 lets a few px of margin.
        width: 320,
      });
    } catch (err) {
      console.warn('GIS renderButton failed:', err);
    }
  }, [gisReady]);

  const isValidEmail = (s) => /\S+@\S+\.\S+/.test(s.trim());

  // Wrap each sign-in handler so we can enforce the terms checkbox
  // and stash the pending consent record before the auth round-trip.
  const guardAndRun = (fn) => {
    if (!agreedToTerms) {
      setShakeTerms(true);
      setTimeout(() => setShakeTerms(false), 600);
      return false;
    }
    recordPendingConsent();
    fn();
    return true;
  };

  const handleGoogle = () => guardAndRun(onGoogleSignIn);
  const handleGuest = () => guardAndRun(onGuestSignIn);

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setShakeTerms(true);
      setTimeout(() => setShakeTerms(false), 600);
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email');
      return;
    }
    setError('');
    setMagicState('sending');
    recordPendingConsent();
    try {
      await onMagicLinkRequest(email.trim());
      setMagicState('sent');
    } catch (err) {
      console.error('Magic link request failed:', err);
      if (err && err.code === 'account-suspended') {
        // The user self-deleted within the last 7 days; Firebase
        // Auth has the user disabled. They can't sign in (even into
        // a "fresh" account) until either the recovery link is
        // clicked or the 7-day window elapses. Tell them that
        // explicitly so they're not stuck wondering why magic-link
        // is silently failing.
        setError('This email was used to delete a listicity account in the last 7 days. Check your inbox for the recovery link to restore it, or wait until the 7-day window ends.');
      } else {
        setError('Could not send the link. Try again or use Google sign-in.');
      }
      setMagicState('idle');
    }
  };

  return (
    <div style={{
      height: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)',
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '40px 44px 36px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
      }}>
        {/* Wordmark — the brand lockup carries enough weight that we can drop
            the old tagline below. Cleaner. */}
        <div style={{
          marginBottom: '32px',
          display: 'flex', justifyContent: 'center',
        }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        {magicState === 'sent' ? (
          <MagicSentScreen
            email={email}
            onAnother={() => { setMagicState('idle'); setEmail(''); }}
          />
        ) : (
          <>
            {/* Primary: Google. Walter 2026-05-16: GIS (Google Identity
                Services) button when configured — bypasses Firebase's
                popup/redirect path that breaks on Android Chrome's
                sessionStorage partition. Falls back to the legacy
                signInWithPopup button if the OAuth client ID isn't
                set yet (during initial config rollout). */}
            {gisConfigured ? (
              <>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                  <div
                    ref={gisButtonRef}
                    style={{
                      minHeight: 44,
                      opacity: agreedToTerms ? 1 : 0.55,
                      transition: 'opacity 0.15s',
                    }}
                  />
                  {!gisReady && !gisError && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--mono)', fontSize: '11px',
                      color: 'var(--text-dimmer)',
                    }}>
                      loading Google sign-in…
                    </div>
                  )}
                  {/* Click-blocking overlay when terms aren't accepted —
                      catches the click, fires the shake, prevents GIS
                      from running. Sits ABOVE the GIS button (z-index)
                      only when !agreedToTerms. */}
                  {!agreedToTerms && (
                    <div
                      onClick={() => { setShakeTerms(true); setTimeout(() => setShakeTerms(false), 600); }}
                      title="Check the agreement box below to continue"
                      style={{
                        position: 'absolute', inset: 0,
                        cursor: 'pointer',
                        background: 'transparent',
                      }}
                    />
                  )}
                </div>
                {/* Walter 2026-05-25 (Bug #11 — round-2 tester report):
                    sign-in errors were rendered as `position: absolute`
                    OVER the GIS button itself, so the error text and
                    the "Sign in with Google" label overlapped and
                    became unreadable. Now in document flow below the
                    button. */}
                {gisError && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 10px',
                    background: 'rgba(192, 50, 50, 0.08)',
                    border: '1px solid var(--danger)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--danger)',
                    fontFamily: 'var(--mono)',
                    fontSize: '11px',
                    lineHeight: 1.5,
                    textAlign: 'left',
                  }}>
                    {gisError}
                  </div>
                )}
              </>
            ) : (
              <ProviderButton
                onClick={handleGoogle}
                accent
                icon={<GoogleIcon />}
                disabled={magicState === 'sending'}
                needsTerms={!agreedToTerms}
                needsTermsTitle="Check the agreement box below to continue"
              >
                Sign in with Google
              </ProviderButton>
            )}

            <Divider>or</Divider>

            {/* Secondary: magic link. Single email field, no password. */}
            <form onSubmit={handleMagicLinkSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="your@email.com"
                autoComplete="email"
                disabled={magicState === 'sending'}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '11px 14px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  color: 'var(--text)',
                  fontFamily: 'var(--mono)',
                  fontSize: '13px',
                  outline: 'none',
                  marginBottom: '8px',
                }}
              />
              <ProviderButton
                type="submit"
                disabled={magicState === 'sending'}
                needsTerms={!agreedToTerms}
                needsTermsTitle="Check the agreement box below to continue"
              >
                {magicState === 'sending' ? 'sending…' : 'Email me a sign-in link'}
              </ProviderButton>
            </form>

            {error && (
              <div style={{
                marginTop: '10px',
                color: 'var(--danger)',
                fontFamily: 'var(--mono)',
                fontSize: '11px',
              }}>
                {error}
              </div>
            )}

            {/* Steve 2026-07-11: a second "or" before the guest option — testers
                kept missing "try without an account". */}
            <Divider>or</Divider>

            {/* Walter 2026-05-26: promoted guest from a small text link
                to a full-width outlined button. Reasoning: a
                frictionless try-without-an-account path is genuinely
                differentiating — most direct competitors require
                sign-up before letting you touch the product. Burying
                it as a tiny underline link below the proper sign-in
                buttons signalled "hidden / not really recommended"
                when actually it's the fastest way for a curious
                visitor to evaluate listicity. Now sits as a clear
                third option, visually distinct from the sign-in
                buttons via a dashed border (echoes the dashed
                head-and-shoulders icon used for guest accounts in the
                app header). Warning copy softened — original was
                "could get accidentally deleted" which scares people
                off; new copy still mentions the device-only / no-sync
                limit but frames it as an upgrade prompt rather than a
                threat. The periodic upgrade nudge inside the app is a
                separate follow-up (parked in BACKLOG). */}
            {signupsPaused ? (
              /* Walter 2026-06-06: sign-ups paused. Existing users use the
                 Google / magic-link options above; the guest "just try it"
                 slot becomes a waitlist invite so new visitors leave their
                 email instead of creating an account. No Terms gate here —
                 joining the waitlist isn't creating an account. */
              <div style={{ marginTop: '18px' }}>
                <ProviderButton
                  onClick={() => onJoinWaitlist && onJoinWaitlist()}
                  dashed
                >
                  join the waitlist
                </ProviderButton>
                <p style={{
                  marginTop: '8px',
                  fontSize: '10px',
                  color: 'var(--text-dimmer)',
                  fontFamily: 'var(--mono)',
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}>
                  new sign-ups are paused · already have an account? sign in above
                </p>
              </div>
            ) : (
              <div style={{ marginTop: '2px' }}>
                <ProviderButton
                  onClick={handleGuest}
                  disabled={magicState === 'sending'}
                  needsTerms={!agreedToTerms}
                  needsTermsTitle="Check the agreement box below to continue"
                >
                  try without an account
                </ProviderButton>
                <p style={{
                  marginTop: '8px',
                  fontSize: '10px',
                  color: 'var(--text-dimmer)',
                  fontFamily: 'var(--mono)',
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}>
                  no sync across devices · sign in later to keep your work safe
                </p>
              </div>
            )}

            {/* Walter 2026-05-15: click-accept Terms checkbox.
                Gates all three sign-in paths (Google / magic-link / guest)
                via the `disabled` and `disabledTitle` props above. When
                shakeTerms is true (user clicked a disabled button), the
                row pulses to draw attention. Pre-checked is intentionally
                NOT used — per GDPR / EU privacy guidance, valid consent
                requires an unticked default. */}
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginTop: '20px',
                padding: '10px 12px',
                background: shakeTerms ? 'var(--accent-dim)' : 'var(--bg)',
                border: shakeTerms ? '1px solid var(--accent)' : '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
                textAlign: 'left',
              }}
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{
                  flexShrink: 0,
                  marginTop: '2px',
                  width: '14px',
                  height: '14px',
                  accentColor: 'var(--accent)',
                  cursor: 'pointer',
                }}
              />
              <span style={{
                fontFamily: 'var(--mono)',
                fontSize: '11px',
                color: 'var(--text-dim)',
                lineHeight: 1.55,
              }}>
                I agree to listicity's{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <p style={{
              marginTop: '20px',
              fontSize: '10px',
              color: 'var(--text-dimmer)',
              fontFamily: 'var(--mono)',
              lineHeight: 1.6,
            }}>
              Your data is private to your account.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ───────── helpers ─────────

function MagicSentScreen({ email, onAnother }) {
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: '14px',
        color: 'var(--accent)',
        fontWeight: 600,
        marginBottom: '10px',
      }}>
        ✓ check your inbox
      </div>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: '12px',
        color: 'var(--text-dim)',
        lineHeight: 1.6,
        marginBottom: '20px',
      }}>
        We sent a sign-in link to <span style={{ color: 'var(--text)' }}>{email}</span>.
        Click it from this device to finish signing in.
      </div>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: '10px',
        color: 'var(--text-dimmer)',
        lineHeight: 1.6,
        marginBottom: '20px',
      }}>
        The link expires in about an hour. If it doesn't arrive, check spam,
        or request a new one below.
      </div>
      <button
        type="button"
        onClick={onAnother}
        style={{
          background: 'none',
          border: '1px solid var(--border-bright)',
          color: 'var(--text-dim)',
          fontFamily: 'var(--mono)',
          fontSize: '11px',
          padding: '8px 14px',
          borderRadius: 'var(--radius)',
          cursor: 'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; }}
      >
        use a different option
      </button>
    </div>
  );
}

function ProviderButton({ children, onClick, type = 'button', accent, icon, disabled, disabledTitle, needsTerms, needsTermsTitle, dashed }) {
  // Walter 2026-05-26 — `needsTerms` is the "looks disabled but isn't"
  // mode for the click-accept-Terms gate. Setting `disabled={true}`
  // (which we did before) makes the browser swallow clicks entirely,
  // which means the form's onSubmit / button's onClick never fire and
  // the shake-the-checkbox feedback never runs. Result: user clicked
  // the magic-link button with Terms unchecked, NOTHING visibly
  // happened. Fix: keep the button clickable, dim it visually
  // (opacity 0.55, default cursor, title hint) and let the click
  // handler decide whether to fire the shake or proceed. The handlers
  // already include the agreedToTerms check + shake trigger via
  // guardAndRun / handleMagicLinkSubmit, so this just lets them run.
  // `dashed` flag swaps the outline border to a dashed style — used
  // for the promoted-guest button so it visually echoes the dashed
  // person icon while staying clearly a button.
  const looksDisabled = disabled || needsTerms;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={
        (needsTerms && needsTermsTitle) ? needsTermsTitle
          : (disabled && disabledTitle) ? disabledTitle
          : undefined
      }
      style={{
        width: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '10px',
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : 'var(--accent)',
        border: accent
          ? '1px solid var(--accent)'
          : (dashed ? '1px dashed var(--accent-dim)' : '1px solid var(--accent-dim)'),
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: '12px',
        fontWeight: accent ? 600 : 400,
        padding: '11px 16px',
        cursor: looksDisabled ? 'default' : 'pointer',
        opacity: looksDisabled ? 0.55 : 1,
        transition: 'background 0.15s, color 0.15s',
      }}
      onMouseEnter={e => {
        if (looksDisabled) return;
        if (accent) {
          e.currentTarget.style.background = 'var(--accent-dim)';
          e.currentTarget.style.borderColor = 'var(--accent-dim)';
        } else {
          e.currentTarget.style.background = 'var(--accent)';
          e.currentTarget.style.color = 'var(--bg)';
        }
      }}
      onMouseLeave={e => {
        if (looksDisabled) return;
        if (accent) {
          e.currentTarget.style.background = 'var(--accent)';
          e.currentTarget.style.borderColor = 'var(--accent)';
        } else {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--accent)';
        }
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function Divider({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      gap: '10px',
      margin: '14px 0',
      fontFamily: 'var(--mono)',
      fontSize: '11px',
      fontWeight: 700,
      color: 'var(--text-dim)',
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
    }}>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
      {children}
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
